import {
  Tabs as TabsBase,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/common/components/ui/tabs";
import { useNavigate, useLocation, Route, Routes } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import QueryString from "qs";
import Skeleton from "../Skeleton";
import { cn } from "@/common/utils/cn";
import TabTrigger from "./components/TabTrigger";

export interface TabsProps {
  tabsList: {
    value: string;
    label: string;
    disabled?: boolean;
    body: JSX.Element | string;
    hidden?: boolean;
    operationKlassToCheckPermission?: string;
  }[];
  defaultValue?: string;
  tabsClassName?: string;
  tabsListClassName?: string;
  tabsListRef?: any;
  tabsTriggerClassName?: string;
  tabsContentClassName?: string;
  keepFilterParams?: boolean;
  filterParamsOnTabChange?: { [key: string]: any; };
  isLoading?: boolean
  skeletonsCount?: number
}

const Tabs = ({
  tabsList,
  defaultValue = '',
  tabsClassName,
  tabsListClassName,
  tabsListRef,
  tabsTriggerClassName,
  tabsContentClassName,
  keepFilterParams,
  filterParamsOnTabChange,
  isLoading,
  skeletonsCount = 10
}: TabsProps) => {
  const { queryString } = useFilterParams();

  const navigate = useNavigate();
  const location = useLocation();

  const initialPath = useMemo(() => {
    const pathname = location.pathname.split('/');
    const lastPath = pathname.pop();
    const tabsListValues = tabsList.map((tab) => tab.value);

    return tabsListValues.includes(lastPath || '') ? pathname.join('/') : location.pathname;
  }, [location.pathname]);

  const currentTab = useMemo(
    () => location.pathname.slice(initialPath.length).split('/').pop(),
    [initialPath, location.pathname]
  );

  useEffect(() => {
    if (!currentTab) {
      if (keepFilterParams) {
        if (filterParamsOnTabChange) {
          navigate(`${defaultValue}?${QueryString.stringify(filterParamsOnTabChange)}`);
        } else {
          navigate(`${defaultValue}?${queryString}`);
        }
      } else {
        navigate(defaultValue);
      }
    }
  }, [currentTab, defaultValue]);

  const handleTabChange = (value: string) => {
    let path = value ? `${initialPath}/${value}` : initialPath;

    if (keepFilterParams) {
      if (filterParamsOnTabChange) {
        path += `?${QueryString.stringify(filterParamsOnTabChange)}`;
      } else {
        path += `?${queryString}`;
      }
    }

    navigate(path);
  };

  return (
    <TabsBase
      value={currentTab || defaultValue}
      onValueChange={handleTabChange}
      className={tabsClassName}
    >
      {isLoading && (
        <TabsList className={tabsListClassName} ref={tabsListRef}>
          {new Array(skeletonsCount).fill(null).map((_, index) => (
            <TabsTrigger
              key={`loading_tab_${index}`}
              value={index.toString()}
              className={cn(tabsTriggerClassName)}
              disabled
            >
              <Skeleton className="bg-white-100" />
            </TabsTrigger>
          ))}
        </TabsList>
      )}
      <div className={cn(isLoading && 'invisible max-h-0')}>
        <TabsList className={tabsListClassName} ref={tabsListRef}>
          {tabsList.filter((tab) => !tab.hidden).map((tab, index) => (
            <TabTrigger
              key={index}
              value={tab.value}
              label={tab.label}
              className={tabsTriggerClassName}
              disabled={tab.disabled}
              operationKlassToCheckPermission={tab.operationKlassToCheckPermission}
            />
          ))}
        </TabsList>
      </div>

      <Routes>
        {tabsList.filter((content) => !content.hidden).map((content, index) => (
          <Route
            key={content.value}
            path={content.value}
            element={(
              <TabsContent
                key={index}
                value={content.value}
                className={tabsContentClassName}
              >
                {content.body}
              </TabsContent>
            )}
          />
        ))}
      </Routes>
    </TabsBase>
  );
};

export default Tabs;
